import React from "react";
import { useUser } from "~/Auth/useUser";
import { useDownloadFile } from "~/file-loading/useDownloadFile";
import { useRawJwt } from "~/Auth/AuthenticationManager";
import { WolfkrowUser } from "~/Auth/WolfkrowUser";
import { apiFetch, HttpError } from "~/api-fetch/apiFetch";

export type ReportFetcherProps = {
  setLoading: React.Dispatch<boolean>;
  fileName: string;
  path?: string;
  body?: Record<string, any>;
  type?: string;
  method?: "post" | "get";
  fullPath?: string;
};

type FetchReportArgs = ReportFetcherProps & {
  token: string | null;
  user: Pick<WolfkrowUser, "isImpersonating" | "email">;
  download: ReturnType<typeof useDownloadFile>;
};

export async function fetchReport({
  token,
  user,
  download,
  setLoading,
  path,
  fileName,
  body,
  type = "application/pdf",
  method,
  fullPath,
}: FetchReportArgs): Promise<void> {
  let res: Response;
  try {
    res = await apiFetch(
      fullPath ?? `/api/${path}`,
      {
        method: method ?? "post",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          impersonation: user.isImpersonating ? user.email : "",
        },
        body: body ? JSON.stringify(body) : undefined,
      },
      { operation: "downloadReport" }
    );
  } finally {
    // Also on a network failure, so the loading indicator does not spin forever.
    setLoading(false);
  }

  if (res.status === 200) {
    const blob = await res.blob();
    const file = new Blob([blob], { type });
    const fileURL = URL.createObjectURL(file);
    download({ linkHref: fileURL, filename: fileName });
  } else {
    throw HttpError.fromResponse(
      res,
      "There was a problem downloading the file"
    );
  }
}

export function useReportFetcher(): (
  props: ReportFetcherProps
) => Promise<void> {
  const token = useRawJwt();
  const user = useUser();
  const download = useDownloadFile();
  return async (props: ReportFetcherProps) => {
    props.setLoading(true);

    // Not awaited: a failure surfaces as an unhandled rejection, which the
    // global error listener classifies and notifies the user about.
    void fetchReport({ ...props, token, user, download });
  };
}
